import React, { useState } from "react";
import { Play, Pause, Stop, SpeakerHigh, MicrophoneStage, Microphone, X, DotsSixVertical, CaretDown, CaretUp, MoonStars } from "@phosphor-icons/react";
import { useAudio } from "../context/AudioContext";
import { groupVoicesByGender } from "../lib/voiceUtils";
import useDraggableFAB from "../hooks/useDraggableFAB";
import { toast } from "sonner";

// Fires a window event that VoiceCommander listens for so the inline
// AudioBar mic button can open the same panel the floating pill would.
// Keeps a single source of truth — no duplicate overlays.
function openVoicePanel() {
  if (typeof window === "undefined") return;
  try { window.dispatchEvent(new CustomEvent("holywords:open-voice")); } catch (_) {}
}

export default function AudioBar() {
  const { isPlaying, isPaused, pause, resume, stop, close, queue, currentIndex, meta, rate, setRate, voices, voiceURI, setVoiceURI, sleepPreset, setSleepTimer } = useAudio();

  const cycleSleep = () => {
    const next = { 0: 15, 15: 30, 30: 60, 60: 0 }[sleepPreset] ?? 15;
    setSleepTimer(next);
    toast(next ? `Sleep timer: stopping in ${next} minutes` : "Sleep timer off");
  };

  // Drag-and-drop position. User report (Feb 26 2026): the AudioBar
  // (Bible TTS player) was pinned at the bottom and covered verses
  // mid-chapter — they expected it to be draggable like the
  // MiniPlayer. We now reuse the same hook, default to clearing the
  // BottomNav, and reserve 96px so the bar can never be dragged back
  // behind the nav.
  const fab = useDraggableFAB({
    // v3: default moved to the TOP of the screen (just under the
    // header/chip rail) so the player never covers the Bible text
    // while reading (user report, June 2026). Key bump resets any
    // saved bottom positions.
    storageKey: "hw:fab:audiobar:v3",
    defaultPosition: { right: 16, top: 84 },
    // Approximate widest mobile width — the bar is `w-[min(720px,
    // calc(100vw - 2rem))]` so this matches the clamp for snapping.
    size: 360,
    bottomReserve: 96,
  });

  // Collapsed = tiny pill (play/pause + progress + expand). Remembered
  // across sessions so listeners who prefer the small pill keep it.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("hw:audiobar:collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem("hw:audiobar:collapsed", c ? "0" : "1"); } catch {}
      return !c;
    });
  };

  // Show the player only while there's an ACTIVE session (playing or
  // paused). When idle — stopped, closed, or a chapter finished — hide
  // it so it doesn't linger on screen after you leave the reader. It
  // stays visible while playing/paused so you can keep listening as
  // you move around the app (incl. the Home screen).
  if (!isPlaying && !isPaused) return null;

  const total = queue.length || 1;
  const progress = Math.min(100, (currentIndex / total) * 100);
  const current = queue[currentIndex];
  const grouped = groupVoicesByGender(voices || []);
  const renderOptions = (list) => list.map((v) => (
    <option key={v.voiceURI} value={v.voiceURI}>
      {v.name.length > 22 ? v.name.slice(0, 22) + "…" : v.name} · {v.lang}
    </option>
  ));

  if (collapsed) {
    return (
      <div
        ref={fab.ref}
        style={fab.style}
        className={`z-40 ${fab.dragging ? "cursor-grabbing" : "cursor-grab"}`}
        data-testid="audio-bar"
      >
        <div className="glass rounded-full border border-cream-300/60 shadow-[0_-6px_24px_rgba(0,0,0,0.16)] pl-1.5 pr-1 py-1 flex items-center gap-1 w-auto">
          <div className="shrink-0 h-8 w-4 grid place-items-center text-muted_ink select-none" data-testid="audio-bar-drag-handle">
            <DotsSixVertical size={14} weight="bold" />
          </div>
          {isPaused || !isPlaying ? (
            <button onClick={resume} data-testid="audio-play-button" data-no-drag className="w-8 h-8 text-forest hover:text-terracotta grid place-items-center transition-all active:scale-95">
              <Play size={16} weight="fill" />
            </button>
          ) : (
            <button onClick={pause} data-testid="audio-pause-button" data-no-drag className="w-8 h-8 text-forest hover:text-terracotta grid place-items-center transition-all active:scale-95">
              <Pause size={16} weight="fill" />
            </button>
          )}
          <div className="w-14 h-1 bg-cream-200 rounded-full overflow-hidden" data-no-drag>
            <div className="h-full bg-terracotta transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <button
            onClick={toggleCollapsed}
            data-testid="audio-bar-expand-button"
            data-no-drag
            className="w-8 h-8 text-ink/70 hover:text-ink grid place-items-center transition-all"
            aria-label="Expand player"
            title="Expand player"
          >
            <CaretUp size={14} weight="bold" />
          </button>
          <button
            onClick={close}
            data-testid="audio-close-button"
            data-no-drag
            className="w-8 h-8 text-ink/70 hover:text-terracotta grid place-items-center transition-all"
            aria-label="Close player"
            title="Close player"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={fab.ref}
      style={fab.style}
      className={`w-[min(420px,calc(100vw-2rem))] md:w-[min(560px,calc(100vw-2rem))] z-40 ${fab.dragging ? "cursor-grabbing" : "cursor-grab"}`}
      data-testid="audio-bar"
    >
      <div className="glass rounded-2xl border border-cream-300/60 shadow-[0_-8px_32px_rgba(0,0,0,0.18)] pl-1.5 pr-2 py-1.5 flex items-center gap-1.5">
        {/* Drag handle — explicit grip column. Everything else is
            marked data-no-drag so taps on the play/stop/mic buttons
            still register as clicks instead of starting a drag. */}
        <div
          className="shrink-0 h-9 w-5 grid place-items-center text-muted_ink hover:text-forest transition-colors select-none"
          title="Drag to move"
          aria-label="Drag the player to move it"
          data-testid="audio-bar-drag-handle"
        >
          <DotsSixVertical size={16} weight="bold" />
        </div>

        <div className="relative" data-no-drag>
          {isPlaying && !isPaused && (
            <span className="absolute inset-0 rounded-full animate-pulse-ring pointer-events-none" />
          )}
          {isPaused || !isPlaying ? (
            <button
              onClick={resume}
              data-testid="audio-play-button"
              data-no-drag
              className="w-9 h-9 text-forest hover:text-terracotta grid place-items-center transition-all hover:scale-105 active:scale-95"
            >
              <Play size={17} weight="fill" />
            </button>
          ) : (
            <button
              onClick={pause}
              data-testid="audio-pause-button"
              data-no-drag
              className="w-9 h-9 text-forest hover:text-terracotta grid place-items-center transition-all hover:scale-105 active:scale-95"
            >
              <Pause size={17} weight="fill" />
            </button>
          )}
        </div>

        <button
          onClick={stop}
          data-testid="audio-stop-button"
          data-no-drag
          className="w-8 h-8 text-ink/70 hover:text-ink grid place-items-center transition-all"
          aria-label="Stop"
        >
          <Stop size={14} weight="fill" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-muted_ink truncate" data-testid="audio-meta">
            {meta ? `${meta.book || meta.title || ""} ${meta.chapter ?? ""}${meta.version ? ` · ${String(meta.version).toUpperCase()}` : ""}` : "Audio"}
            {current ? ` · ${current.id ? `v${current.id}` : ""}` : ""}
            <span className="ml-1.5 tracking-wider uppercase text-[9px]">
              {currentIndex + 1 >= total ? "Done" : `${Math.min(currentIndex + 1, total)}/${total}`}
            </span>
          </div>
          <div className="mt-1 h-1 bg-cream-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-terracotta transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {voices && voices.length > 0 && (
          <div className="hidden md:flex items-center gap-1 px-2 max-w-[200px]" data-no-drag>
            <MicrophoneStage size={14} className="text-muted_ink" />
            <select
              value={voiceURI || ""}
              onChange={(e) => setVoiceURI(e.target.value || null)}
              data-testid="audio-voice-select"
              data-no-drag
              className="bg-transparent text-xs font-medium text-ink outline-none cursor-pointer truncate w-full"
              title="Choose a voice"
            >
              <option value="">Auto voice</option>
              {grouped.female.length > 0 && (
                <optgroup label="Woman">
                  {renderOptions(grouped.female)}
                </optgroup>
              )}
              {grouped.male.length > 0 && (
                <optgroup label="Man">
                  {renderOptions(grouped.male)}
                </optgroup>
              )}
              {grouped.other.length > 0 && (
                <optgroup label="Other / Neutral">
                  {renderOptions(grouped.other)}
                </optgroup>
              )}
            </select>
          </div>
        )}

        <div className="hidden sm:flex items-center gap-1 px-2" data-no-drag>
          <SpeakerHigh size={14} className="text-muted_ink" />
          <select
            value={rate}
            onChange={(e) => setRate(parseFloat(e.target.value))}
            data-testid="audio-speed-select"
            data-no-drag
            className="bg-transparent text-xs font-medium text-ink outline-none cursor-pointer"
          >
            <option value="0.75">0.75x</option>
            <option value="0.95">1x</option>
            <option value="1.25">1.25x</option>
            <option value="1.5">1.5x</option>
          </select>
        </div>

        <button
          onClick={openVoicePanel}
          data-testid="audio-bar-voice-button"
          data-no-drag
          aria-label="Voice commands"
          title="Voice commands"
          className="shrink-0 w-8 h-8 text-terracotta hover:text-terracotta/80 grid place-items-center transition-all hover:scale-105 active:scale-95"
        >
          <Microphone size={13} weight="fill" />
        </button>

        <button
          onClick={cycleSleep}
          data-testid="audio-sleep-timer-button"
          data-no-drag
          aria-label="Sleep timer"
          title="Sleep timer"
          className={`relative w-8 h-8 grid place-items-center transition-all ${sleepPreset ? "text-terracotta" : "text-ink/70 hover:text-ink"}`}
        >
          <MoonStars size={14} weight={sleepPreset ? "fill" : "regular"} />
          {sleepPreset > 0 && (
            <span className="absolute -top-0.5 -right-0.5 text-[8px] font-bold bg-terracotta text-white rounded-full px-1 leading-[10px] py-[1px]">{sleepPreset}</span>
          )}
        </button>

        <button
          onClick={toggleCollapsed}
          data-testid="audio-bar-collapse-button"
          data-no-drag
          className="w-8 h-8 text-ink/70 hover:text-ink grid place-items-center transition-all"
          aria-label="Collapse player"
          title="Collapse player"
        >
          <CaretDown size={14} weight="bold" />
        </button>

        <button
          onClick={close}
          data-testid="audio-close-button"
          data-no-drag
          className="w-8 h-8 text-ink/70 hover:bg-terracotta hover:text-cream grid place-items-center transition-all"
          aria-label="Close player"
          title="Close player"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
